import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSlate } from 'slate-react';
import EditorButton from '../EditorButton/EditorButton';
import '../RichTextEditor.scss';
import './Table.scss';
import { insertTable, isInTable } from './table.utils';

// ============================================================================
// Constants
// ============================================================================

const MAX_ROWS = 6;
const MAX_COLS = 8;
const CELL_SIZE = 20;
const CELL_GAP = 2;
const DROPDOWN_PADDING = 16;
const DROPDOWN_GAP = 4;
const VIEWPORT_MARGIN = 10;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate optimal dropdown position to keep it within viewport
 */
const calculateDropdownPosition = (buttonRect) => {
  const dropdownWidth = MAX_COLS * CELL_SIZE + (MAX_COLS - 1) * CELL_GAP + DROPDOWN_PADDING;

  let x = buttonRect.left;

  // Shift left if would overflow right edge
  if (x + dropdownWidth > window.innerWidth) {
    x = Math.max(VIEWPORT_MARGIN, window.innerWidth - dropdownWidth - VIEWPORT_MARGIN);
  }

  // Ensure minimum left margin
  x = Math.max(VIEWPORT_MARGIN, x);

  return {
    x,
    y: buttonRect.bottom + DROPDOWN_GAP,
  };
};

// ============================================================================
// Table Button Component
// ============================================================================

const TableButton = () => {
  const editor = useSlate();
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredCell, setHoveredCell] = useState({ row: 0, col: 0 });
  const [dropdownPos, setDropdownPos] = useState({ x: 0, y: 0 });

  const handleButtonClick = useCallback(
    (e) => {
      e.preventDefault();

      if (isInTable(editor)) {
        return; // Don't allow nested tables
      }

      if (!isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPos(calculateDropdownPosition(rect));
      }

      setIsOpen(!isOpen);
      setHoveredCell({ row: 0, col: 0 });
    },
    [editor, isOpen]
  );

  const handleCellHover = useCallback((row, col) => {
    setHoveredCell({ row, col });
  }, []);

  const handleCellClick = useCallback(
    (e, row, col) => {
      e.preventDefault();
      insertTable(editor, row + 1, col + 1);
      setIsOpen(false);
      setHoveredCell({ row: 0, col: 0 });
    },
    [editor]
  );

  // Close dropdown when clicking outside button or dropdown
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (e) => {
      const clickedInside =
        buttonRef.current?.contains(e.target) || dropdownRef.current?.contains(e.target);

      if (!clickedInside) {
        setIsOpen(false);
      }
    };

    // Delay to avoid immediate close on open
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Generate grid cells
  const gridCells = useMemo(() => {
    const cells = [];
    for (let row = 0; row < MAX_ROWS; row++) {
      for (let col = 0; col < MAX_COLS; col++) {
        const isHighlighted = row <= hoveredCell.row && col <= hoveredCell.col;
        cells.push(
          <div
            key={`${row}-${col}`}
            className={`grid-cell${isHighlighted ? ' highlighted' : ''}`}
            onMouseEnter={() => handleCellHover(row, col)}
            onMouseDown={(e) => handleCellClick(e, row, col)}
          />
        );
      }
    }
    return cells;
  }, [hoveredCell.row, hoveredCell.col, handleCellHover, handleCellClick]);

  const isActive = isInTable(editor);
  const gridLabel =
    hoveredCell.row > 0 || hoveredCell.col > 0
      ? `${hoveredCell.row + 1} × ${hoveredCell.col + 1}`
      : 'Select size';

  return (
    <div
      className='table-button-wrapper'
      ref={buttonRef}
    >
      <EditorButton
        active={isActive}
        onMouseDown={handleButtonClick}
        title='Insert table'
      >
        <span className='editor-icon material-icons'>table_chart</span>
      </EditorButton>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            className='table-grid-dropdown'
            ref={dropdownRef}
            style={{ top: `${dropdownPos.y}px`, left: `${dropdownPos.x}px` }}
          >
            <div className='grid-size-label'>{gridLabel}</div>
            <div className='grid-container'>{gridCells}</div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TableButton;
